import { isPlatformServer } from '@angular/common'
import { inject, PLATFORM_ID } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  createUrlTreeFromSnapshot,
  RedirectCommand,
  Route,
  Router,
} from '@angular/router'

import { EmptyRouteForSsrPreventionComponent } from './empty-route-for-ssr-prevention.component'

const EMPTY_ROUTE_PATH = '___empty_route_for_ssr___'
const NAVIGATION_STATE_KEY_SSR_SILENT_REDIRECT = 'ssrSilendRedirect'

/** this route is used as target for the silent redirect in {@link childRouteSsrPreventionGuard} */
export const EMPTY_ROUTE_FOR_SSR_PREVENTION = {
  path: EMPTY_ROUTE_PATH,
  canActivate: [
    (route) =>
      isPlatformServer(inject(PLATFORM_ID)) ? true : new RedirectCommand(createUrlTreeFromSnapshot(route, ['/'])),
  ],
  component: EmptyRouteForSsrPreventionComponent,
} as const satisfies Route

/**
 * this guard prevents the resolving and rendering of any child route in the Server Context
 * This is achieved with a silent redirect to an empty route.
 * Therefore, it requires to have the {@link EMPTY_ROUTE_FOR_SSR_PREVENTION} route as child route configured.
 */
export const childRouteSsrPreventionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const [platformId, router] = [inject(PLATFORM_ID), inject(Router)]

  const navState = router.currentNavigation()?.extras.state ?? {}

  if (isPlatformServer(platformId) && navState[NAVIGATION_STATE_KEY_SSR_SILENT_REDIRECT] !== true) {
    // config check but only in dev mode
    if (ngDevMode && !route.routeConfig?.children?.some((r) => r.path === EMPTY_ROUTE_PATH)) {
      throw new Error('missing EMPTY_ROUTE_FOR_SSR_PREVENTION inside route children')
    }

    return new RedirectCommand(createUrlTreeFromSnapshot(route, ['.', EMPTY_ROUTE_PATH]), {
      skipLocationChange: true, // crucial --> otherwise we fail
      state: { [NAVIGATION_STATE_KEY_SSR_SILENT_REDIRECT]: true },
    })
  }
  return true
}
